import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './core/query/queryClient';
import { StoreProvider } from './context/StoreContext';
import { PermissionProvider } from './context/PermissionContext';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider, useToast } from './components/Toast';
import Login from './pages/Login';
import Terms from './pages/Terms';
import RefundPolicy from './pages/RefundPolicy';
import SelectStore from './pages/SelectStore';
import SwUpdateBanner from './components/SwUpdateBanner';
import OfflineQueueBanner from './components/OfflineQueueBanner';
import InstallPrompt from './components/InstallPrompt';
import AdminDashboard from './pages/AdminDashboard';
import AppShell from './core/layout/AppShell';
import { AuthProvider } from './core/auth';
import { TenantProvider } from './core/context/TenantContext';
import TenantModuleRouter from './routes/TenantModuleRouter';
import './App.css';

// Listens for aiviora:api-error events dispatched by the Axios interceptor
// and shows a toast. Must be rendered inside <ToastProvider>.
function ApiErrorToastListener() {
  const showToast = useToast();
  useEffect(() => {
    const handler = (e) => showToast(e.detail.message, 'error');
    window.addEventListener('aiviora:api-error', handler);
    return () => window.removeEventListener('aiviora:api-error', handler);
  }, [showToast]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <StoreProvider>
          <PermissionProvider>
            <TenantProvider>
              <ToastProvider>
                <ApiErrorToastListener />
                <SwUpdateBanner />
                <OfflineQueueBanner />
                <InstallPrompt />
                <Router>
                  <Routes>
                    <Route path="/public" element={<Navigate to="/login" replace />} />
                    <Route path="/login" element={<Login />} />
                    {/* Public legal pages — no auth required */}
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route
                      path="/select-store"
                      element={(
                        <PrivateRoute>
                          <SelectStore />
                        </PrivateRoute>
                      )}
                    />
                    <Route
                      path="/admin"
                      element={(
                        <PrivateRoute>
                          <AdminDashboard />
                        </PrivateRoute>
                      )}
                    />

                    <Route
                      path="/"
                      element={(
                        <PrivateRoute requireStore>
                          <AppShell />
                        </PrivateRoute>
                      )}
                    >
                      <Route path="*" element={<TenantModuleRouter />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Router>
              </ToastProvider>
            </TenantProvider>
          </PermissionProvider>
        </StoreProvider>
      </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
